import { useReq } from "@larner.dev/use-req";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  List,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ProfilePictureKey,
  UpcomingSparkForUser,
  PastSparkForUser,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { apiReq } from "../../../lib/apiReq";
import { getApiErrorMessage } from "../../../lib/error-translators/getApiErrorMessage";
import { toaster } from "../../../lib/toaster";
import { AuthenticationDialog } from "../../../components/AuthenticationDialog/AuthenticationDialog";
import { Meta } from "../../../components/Meta/Meta";
import { useHideNav } from "../../../lib/useHideNav";
import { store, useStoreData } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { isLoggedIn } from "../../../lib/isLoggedIn";
import { ExternalEmailVerificationDialog } from "./ExternalEmailVerificationDialog";
import picture1 from "./images/picture1.png";
import picture2 from "./images/picture2.png";
import picture3 from "./images/picture3.png";
import gfLogo from "../../../assets/logo.png";

interface ExternalRegistrationInfo {
  external_registration: {
    id: number;
    email: string;
    first_name: string;
    spark_id: number;
    source: string;
    token_fresh: boolean;
  };
  spark: {
    id: number;
    title: string;
    activity: string;
    location: string;
    starts_at: string;
    ends_at: string;
  };
  organization: {
    id: number;
    name: string;
    slug: string;
    logo_url: string;
  };
}

const CLAIM_ERRORS: Record<string, string> = {
  REGISTRATION_NOT_FOUND:
    "This registration link is invalid or has already been used.",
  REGISTRATION_ALREADY_CLAIMED:
    "This registration has already been claimed. If you believe this is an error, please contact us for help.",
  INVALID_TOKEN: "Invalid verification code. Please try again.",
};

const featureRows = [
  {
    image: picture1,
    title: "See who was there",
    description:
      "Everyone from the event, all in one place. No bots, just people who showed up.",
  },
  {
    image: picture2,
    title: "Pick your people",
    description: "Choose who you'd be into seeing again.",
  },
  {
    image: picture3,
    title: "Mutual = match",
    description:
      "If they pick you too, you're connected. That's it. No overthinking required.",
  },
];

export const ClaimRegistration = () => {
  useHideNav();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isOptOut = searchParams.get("opt_out") === "1";
  const navigate = useNavigate();
  const storeData = useStoreData();
  const [initialLoad, setInitialLoad] = useState(true);
  const loggedIn = useMemo(() => isLoggedIn(storeData), [storeData]);

  const [infoReq, infoReqState] = useReq<ExternalRegistrationInfo>(apiReq);
  const [optOutReq, optOutReqState] = useReq<{ success: boolean }>(apiReq);

  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [claimReq, claimReqState] = useReq<
    | {
        token: string;
        user: UserWithMediaAndGroups;
        upcomingSparks: UpcomingSparkForUser[];
        pastSparks: PastSparkForUser[];
        requires_verification: false;
      }
    | {
        credential: { id: number };
        external_registration_id: number;
        requires_verification: true;
      }
  >(apiReq);
  const [verificationState, setVerificationState] = useState<{
    credentialId: number;
    email: string;
  } | null>(null);

  const handleAuthenticated = useCallback(async () => {
    if (!token) return;
    const result = await claimReq.post("/external-registration/claim", {
      token,
    });
    if (!result.success) {
      toaster.error({
        title: "Unable to Claim Registration",
        description: getApiErrorMessage(result.error, CLAIM_ERRORS),
      });
      throw result.error;
    }
    if (result.result.requires_verification) {
      setShowAuthDialog(false);
      setVerificationState({
        credentialId: result.result.credential.id,
        email: infoReqState.result!.external_registration.email,
      });
      throw new Error("REQUIRES_EXTERNAL_VERIFICATION");
    }
    // Push the freshly-claimed registration into the store so any reader
    // of `upcomingSparks` (e.g. the registered-spark check below, EventV2)
    // sees it without an extra round-trip.
    store.dispatch({
      type: ActionType.UpdateUserState,
      params: {
        user: result.result.user,
        upcomingSparks: result.result.upcomingSparks,
        pastSparks: result.result.pastSparks,
      },
    });
  }, [token, claimReq, infoReqState.result]);

  const emailMatchesLoggedInUser =
    loggedIn &&
    !!infoReqState.result?.external_registration.email &&
    storeData.user?.email?.toLowerCase() ===
      infoReqState.result.external_registration.email.toLowerCase();

  const profileIncomplete =
    !storeData.user?.birth_date ||
    !storeData.user?.gender ||
    !storeData.user?.gender_preference ||
    !storeData.user?.media?.[ProfilePictureKey.Headshot];

  const handleSetupProfile = useCallback(async () => {
    if (emailMatchesLoggedInUser) {
      try {
        await handleAuthenticated();
      } catch {
        return;
      }
      if (profileIncomplete) {
        setShowAuthDialog(true);
      } else {
        navigate("/register/complete");
      }
    } else {
      setShowAuthDialog(true);
    }
  }, [
    emailMatchesLoggedInUser,
    handleAuthenticated,
    profileIncomplete,
    navigate,
  ]);

  useEffect(() => {
    (async () => {
      if (token && isOptOut) {
        await optOutReq.post("/external-registration/opt-out", { token });
        setInitialLoad(false);
        return;
      }
      if (token) {
        const result = await infoReq.get(`/external-registration/${token}`);
        if (!result.success) {
          if (result.error.code === "REGISTRATION_ALREADY_CLAIMED") {
            const claimedSparkId = result.error.params?.spark_id;
            if (!!claimedSparkId && loggedIn) {
              const userIsRegisteredForSpark = !!store.data.upcomingSparks?.some(
                (s) => s.id === claimedSparkId && s.registered,
              );
              if (userIsRegisteredForSpark) {
                navigate("/register/complete?profileExists=true", {
                  replace: true,
                });
                return;
              }
            }
          }
        }
      }
      setInitialLoad(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Loading state
  if (initialLoad) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  // Opt-out result
  if (isOptOut && !initialLoad) {
    return (
      <Flex justify="center" p="2rem">
        <Box
          bg="white"
          borderRadius="10px"
          border="2px solid"
          borderColor="gray.200"
          p="2rem"
          w="100%"
          maxW="35rem"
          textAlign="center"
        >
          {optOutReqState.result?.success ? (
            <>
              <Heading as="h2" textStyle="headingMd" mb="1rem">
                You've Been Opted Out
              </Heading>
              <Text textStyle="body">
                You will no longer receive emails from Green Flag.
              </Text>
            </>
          ) : (
            <>
              <Heading as="h2" textStyle="headingMd" mb="1rem">
                Unable to Opt Out
              </Heading>
              <Text textStyle="body">
                {optOutReqState.error
                  ? getApiErrorMessage(optOutReqState.error, CLAIM_ERRORS)
                  : "Something went wrong. Please try again."}
              </Text>
            </>
          )}
        </Box>
      </Flex>
    );
  }

  // Error loading registration info
  if (infoReqState.error) {
    return (
      <Flex justify="center" p="2rem">
        <Box
          bg="white"
          borderRadius="10px"
          border="2px solid"
          borderColor="gray.200"
          p="2rem"
          w="100%"
          maxW="35rem"
          textAlign="center"
        >
          <Heading as="h2" textStyle="headingMd" mb="1rem">
            Unable to Load Registration
          </Heading>
          <Text textStyle="body">
            {getApiErrorMessage(infoReqState.error, CLAIM_ERRORS)}
          </Text>
          {infoReqState.error?.code === "REGISTRATION_ALREADY_CLAIMED" && (
            <Button
              mt="1.5rem"
              colorPalette="greenDark"
              onClick={() => navigate("/contact")}
            >
              Contact Us
            </Button>
          )}
        </Box>
      </Flex>
    );
  }

  const info = infoReqState.result;
  if (!info) return null;

  return (
    <Flex direction="column" align="center" bg="white">
      <Meta>
        <title>
          Claim Your Profile - {info.organization.name} | Green Flag
        </title>
      </Meta>
      {/* Header: Organization logo + powered by badge + CTA */}
      <Flex
        direction="column"
        align="center"
        gap="1.25rem"
        py="2.5rem"
        px="1.5rem"
        w="100%"
        maxW="35rem"
      >
        {info.organization.logo_url && (
          <Image
            src={info.organization.logo_url}
            alt={info.organization.name}
            maxH="120px"
            objectFit="contain"
          />
        )}
        <Flex align="center" gap="0.35rem">
          <Text fontSize="16px" fontWeight="500" color="greenDark.solid">
            matching powered by
          </Text>
          <Image src={gfLogo} alt="Green Flag" h="24px" />
        </Flex>
        <Button
          colorPalette="greenLight"
          size="sm"
          px="1.5rem"
          textTransform="none"
          color="white"
          onClick={handleSetupProfile}
          disabled={claimReqState.loading}
          loading={claimReqState.loading}
        >
          Setup Your Profile
        </Button>

        {/* Event Details */}
        <Box
          w="100%"
          bg="#F5F5F5"
          borderRadius="10px"
          p="1.25rem"
        >
          <Text
            fontSize="10px"
            fontWeight="600"
            textTransform="uppercase"
            letterSpacing="0.05em"
            color="gray.500"
            mb="0.5rem"
          >
            Event Details
          </Text>
          <Text
            fontFamily="body"
            fontSize="12px"
            fontWeight="600"
            color="gray.800"
            lineHeight="1.3"
            mb="0.25rem"
          >
            {info.spark.title}
          </Text>
          <Text
            fontSize="12px"
            fontWeight="500"
            color="brand.gray.medium"
          >
            {new Date(info.spark.starts_at).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
            })}
            {" \u2022 "}
            {new Date(info.spark.starts_at).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
            })}
          </Text>
        </Box>
      </Flex>

      {/* Green band: Hero heading */}
      <Box w="100%" bg="greenLight.solid">
        <Flex
          direction="column"
          align="center"
          gap="1rem"
          py="2rem"
          px="1.5rem"
          maxW="35rem"
          mx="auto"
        >
          <Heading
            as="h1"
            fontFamily="body"
            fontSize="24px"
            fontWeight="600"
            lineHeight="1.3"
            color="white"
            textAlign="center"
          >
            Meet people at singles events.
            <br />
            Actually connect after.
          </Heading>
        </Flex>
      </Box>

      {/* Feature rows */}
      <Flex
        direction="column"
        gap="2rem"
        w="100%"
        maxW="35rem"
        px="1.5rem"
        pt="2rem"
        pb="1rem"
      >
        {featureRows.map((feature, index) => (
          <Flex key={index} align="start" gap="1rem">
            <Image
              src={feature.image}
              alt={feature.title}
              w="120px"
              h="120px"
              objectFit="cover"
              flexShrink={0}
            />
            <Box>
              <Text
                fontFamily="body"
                fontSize="24px"
                fontWeight="700"
                lineHeight="1.2"
                color="greenDark.solid"
                mb="0.25rem"
              >
                {feature.title}
              </Text>
              <Text fontSize="16px" color="greenDark.solid" lineHeight="1.4">
                {feature.description}
              </Text>
            </Box>
          </Flex>
        ))}
      </Flex>

      {/* Stats section */}
      <Box w="100%" bg="greenLight.solid">
        <Flex
          direction="column"
          align="center"
          py="2rem"
          px="1.5rem"
          maxW="35rem"
          mx="auto"
        >
          <Heading
            as="h2"
            fontFamily="body"
            fontSize="24px"
            fontWeight="600"
            lineHeight="1.3"
            color="white"
            textAlign="center"
            mb="1.5rem"
          >
            On average, 42% of singles get a mutual match on Green Flag
          </Heading>
          <Box textAlign="left" color="white">
            <Text
              fontFamily="body"
              fontSize="16px"
              fontWeight="400"
              mb="0.75rem"
            >
              Why is Green Flag better?
            </Text>
            <Text fontFamily="body" fontSize="16px" mb="0.5rem">
              You're not starting from zero.
            </Text>
            <Text fontFamily="body" fontSize="16px" mb="0.5rem">
              You've already:
            </Text>
            <List.Root
              ps="1.25rem"
              mb="0.75rem"
              color="white"
              css={{ "& li::marker": { color: "white" } }}
            >
              <List.Item>
                <Text fontFamily="body" fontSize="16px" color="white">
                  made eye contact
                </Text>
              </List.Item>
              <List.Item>
                <Text fontFamily="body" fontSize="16px" color="white">
                  had a conversation
                </Text>
              </List.Item>
              <List.Item>
                <Text fontFamily="body" fontSize="16px" color="white">
                  felt the vibe
                </Text>
              </List.Item>
            </List.Root>
            <Text fontFamily="body" fontSize="16px">
              This just makes sure a good connection doesn't get lost.
            </Text>
          </Box>
          <Button
            bg="white"
            color="greenLight.solid"
            size="sm"
            px="1.5rem"
            mt="1.5rem"
            textTransform="none"
            onClick={handleSetupProfile}
            disabled={claimReqState.loading}
            loading={claimReqState.loading}
          >
            Setup Your Profile
          </Button>
        </Flex>
      </Box>

      <AuthenticationDialog
        isShown={showAuthDialog}
        onClose={() => setShowAuthDialog(false)}
        onAuthenticated={handleAuthenticated}
        onComplete={() => navigate("/register/complete")}
        title="Setup Your Profile"
        confirmLabel="Continue"
        source={info.external_registration.source}
        defaultEmail={info.external_registration.email}
        screens={["details", "genderPreference", "profilePhotos"]}
        startScreen={
          loggedIn &&
          storeData.user?.email?.toLowerCase() !==
            info.external_registration.email.toLowerCase()
            ? "email"
            : undefined
        }
      />
      {verificationState && (
        <ExternalEmailVerificationDialog
          isShown={!!verificationState}
          onClose={() => setVerificationState(null)}
          onComplete={() => navigate("/register/complete")}
          email={verificationState.email}
          credentialId={verificationState.credentialId}
          registrationToken={token!}
        />
      )}
    </Flex>
  );
};
